import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SequencePlayer.module.css';
import PerspectiveCarousel from '../ContentDisplay/PerspectiveCarousel';

const perspectiveLabels = {
  front: 'FRONT',
  side: 'SIDE',
  rear: 'REAR',
  '45_degree': '45°',
};

const SequencePlayer = ({
  items = [],
  selectedItemId = null,
  selectedPerspective = 'front',
  onSelectItem,
  onPerspectiveChange,
}) => {
  const [isContinuousPlaying, setIsContinuousPlaying] = useState(false);
  const [stepDurationSeconds, setStepDurationSeconds] = useState(3);
  const intervalRef = useRef(null);
  const playableItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const foundIndex = playableItems.findIndex(
    (item) => Number(item.sequence_item_id) === Number(selectedItemId)
  );

  const selectedIndex = foundIndex >= 0 ? foundIndex : 0;
  const currentItem = playableItems[selectedIndex];

  const displayPerspective =
    perspectiveLabels[selectedPerspective] || String(selectedPerspective || '').toUpperCase();

  const goToIndex = (index) => {
    if (!playableItems[index]) return;
    if (onSelectItem) onSelectItem(playableItems[index]);
  };

  const goToPrevious = () => {
    goToIndex(selectedIndex - 1);
  };

  const goToNext = () => {
    goToIndex(selectedIndex + 1);
  };

  const playStep = () => {
    if (selectedIndex < playableItems.length - 1) {
      goToNext();
    } else {
      setIsContinuousPlaying(false);
    }
  };

  const toggleContinuousPlay = () => {
    setIsContinuousPlaying((prev) => !prev);
  };

  const pause = () => {
    setIsContinuousPlaying(false);
  };

  const restart = () => {
    setIsContinuousPlaying(false);
    goToIndex(0);
  };

  useEffect(() => {
    if (!isContinuousPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      return;
    }

    intervalRef.current = setInterval(() => {
      const currentIndex = playableItems.findIndex(
        (item) => Number(item.sequence_item_id) === Number(selectedItemId)
      );

      if (currentIndex < 0 || currentIndex >= playableItems.length - 1) {
        setIsContinuousPlaying(false);
        return;
      }

      if (onSelectItem) {
        onSelectItem(playableItems[currentIndex + 1]);
      }
    }, Math.max(1, Number(stepDurationSeconds) || 3) * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    isContinuousPlaying,
    selectedItemId,
    playableItems,
    onSelectItem,
    stepDurationSeconds,
  ]);

  useEffect(() => {
    if (selectedIndex >= playableItems.length - 1 && isContinuousPlaying) {
      setIsContinuousPlaying(false);
    }
  }, [selectedIndex, playableItems.length, isContinuousPlaying]);

  if (playableItems.length === 0) return null;

  return (
    <div className={styles.playerWrapper}>
      <div className={styles.playerHeader}>
        <span className={styles.playerTitle}>Sequence Player</span>

        <div className={styles.playerMeta}>
          <span className={styles.perspectiveBadge}>
            Perspective: {displayPerspective}
          </span>

          <span className={styles.playerPosition}>
            {selectedIndex + 1} / {playableItems.length}
          </span>
        </div>
      </div>

      <div className={styles.playerBody}>
        <button
          type="button"
          className={styles.sideButton}
          onClick={goToPrevious}
          disabled={selectedIndex <= 0}
        >
          Previous
        </button>

        <div className={styles.playerStage}>
          <div className={styles.currentName}>
            {currentItem.name}
          </div>

          <div className={styles.mediaArea}>
            {currentItem?.content_id ? (
              <PerspectiveCarousel
                key={`sequence-player-${currentItem.content_id}-${currentItem.sequence_item_id}`}
                content={currentItem}
                preferredPerspective={selectedPerspective}
                onPerspectiveChange={(nextPerspective) => {
                  if (onPerspectiveChange) {
                    onPerspectiveChange(nextPerspective);
                  }
                }}
              />
            ) : (
              <div className={styles.previewPlaceholder}>
                No content_id available for this sequence item.
              </div>
            )}
          </div>

          <div className={styles.controlsRow}>
            <button
              type="button"
              className={styles.controlButton}
              onClick={restart}
              disabled={selectedIndex <= 0}
            >
              Restart
            </button>

            <button
              type="button"
              className={styles.controlButton}
              onClick={goToPrevious}
              disabled={selectedIndex <= 0}
            >
              ◀ Previous
            </button>

            <button
              type="button"
              className={styles.controlButton}
              onClick={playStep}
              disabled={selectedIndex >= playableItems.length - 1}
              title="Advance one sequence item"
            >
              Step ▶
            </button>

            <button
              type="button"
              className={`${styles.controlButton} ${
                isContinuousPlaying ? styles.activeButton : ''
              }`}
              onClick={toggleContinuousPlay}
              disabled={playableItems.length <= 1 || selectedIndex >= playableItems.length - 1}
              title="Advance continuously through the sequence"
            >
              {isContinuousPlaying ? 'Playing...' : 'Play All ▶▶'}
            </button>

            <button
              type="button"
              className={styles.controlButton}
              onClick={pause}
              disabled={!isContinuousPlaying}
            >
              Pause
            </button>

            <button
              type="button"
              className={styles.controlButton}
              onClick={goToNext}
              disabled={selectedIndex >= playableItems.length - 1}
            >
              Next ▶
            </button>
          </div>

          <div className={styles.settingsRow}>
            <label className={styles.durationLabel}>
              Step duration
              <input
                type="number"
                min="1"
                max="60"
                value={stepDurationSeconds}
                onChange={(e) => {
                  setStepDurationSeconds(e.target.value);
                }}
                className={styles.durationInput}
              />
              seconds
            </label>
          </div>
        </div>

        <button
          type="button"
          className={styles.sideButton}
          onClick={goToNext}
          disabled={selectedIndex >= playableItems.length - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SequencePlayer;
